import { ActionIcon, Stack, Text, Title, Tooltip, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconClipboardText } from '@tabler/icons-react'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { isAddress } from 'viem'

import ViewApplicationModal from '@/components/Modals/Governance/ViewApplicationModal'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import useProfileDetails from '@/hooks/useProfileDetails'
import useProfilePath from '@/hooks/useProfilePath'
import { useAuth } from '@/plugins/auth'
import { shortenHash, startsWithIpfsOrNot } from '@/utils/utils'
import utils from '@/utils/utils.module.css'

import Loading from '../../Loading'
import classes from './UserDetails.module.css'

interface UserDetailsProps {
  userId: string
  hideCreatorName?: boolean
  hidePfp?: boolean
  hideUsername?: boolean
  showApplication?: boolean
  addInfo?: React.ReactNode
  pfpSize?: string
  width?: string
  cardNavigation?: () => void
}

const UserDetails: React.FC<UserDetailsProps> = ({
  userId,
  hideCreatorName = false,
  hidePfp = false,
  hideUsername = false,
  showApplication = false,
  addInfo = undefined,
  pfpSize = '45px',
  width = '100%',
  cardNavigation = () => {},
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const { address } = useAuth()

  // Governance application
  const { nominationData, electionData } = useGovernanceContext()
  const userHasApplication = useMemo(() => {
    const nomineeData = nominationData.filter(
      cand => cand.nominee.toLowerCase() === userId?.toLowerCase()
    )[0]
    if (nomineeData) {
      return nomineeData.cid !== ''
    }
    return false
  }, [nominationData, userId])

  const [openedApplication, { open: openApplication, close: closeApplication }] =
    useDisclosure(false)
  const creatorHasProfile = useMemo(() => isAddress(userId), [userId])

  const { isLoadingProfileInfo, creatorPfp, creatorName, creatorUsername } =
    useProfileDetails(userId)
  const profilePath = useProfilePath(creatorHasProfile ? userId : address)

  const handleNavigation = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (cardNavigation) {
        cardNavigation()
      }
      if (creatorHasProfile && profilePath && !cardNavigation) {
        navigate(profilePath)
      }
    },
    [profilePath, creatorHasProfile, cardNavigation]
  )

  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const profileImageSize = useMemo(() => (hidePfp ? '0px' : pfpSize), [hidePfp, pfpSize])
  const gapPfpAndUsername = useMemo(() => (hideCreatorName ? '5px' : '20px'), [hideCreatorName])
  const usernameWidth = useMemo(
    () => `calc(${width} - ${profileImageSize} - ${gapPfpAndUsername})`,
    [width, profileImageSize, gapPfpAndUsername]
  )

  // Voting period started, so users cannot fill applications anymore or edit
  const fillApplicationPeriodEnded = useMemo(() => {
    const now = new Date().getTime() / 1000
    return electionData.campaignTime < now
  }, [electionData])

  return (
    <Stack
      style={{
        flexDirection: 'row',
        gap: gapPfpAndUsername,
        alignItems: 'center',
        cursor: creatorHasProfile && !cardNavigation ? 'pointer' : 'default',
        width,
      }}
      onClick={handleNavigation}
    >
      {creatorHasProfile ? (
        <>
          {!hidePfp && (
            <>
              {isLoadingProfileInfo ? (
                <Loading size={profileImageSize} />
              ) : (
                <Stack
                  style={{ width: profileImageSize, cursor: 'pointer' }}
                  onClick={() => navigate(profilePath)}
                >
                  <img
                    src={startsWithIpfsOrNot(creatorPfp)}
                    style={{ width: profileImageSize, height: profileImageSize, borderRadius: 6 }}
                    alt="ppf"
                  />
                </Stack>
              )}
            </>
          )}
          <Stack gap="0px" style={{ width: usernameWidth }}>
            {!hideCreatorName && (
              <>
                <Title
                  order={4}
                  className={utils.truncate}
                  // style={{ width: 'fit-content' }}
                  onClick={() => navigate(profilePath)}
                >
                  {creatorName}
                  {userHasApplication && showApplication && (
                    <Tooltip
                      label={t('components.profile.viewApplication', 'View Application')}
                      position="top"
                    >
                      <ActionIcon
                        onClick={e => {
                          e.stopPropagation()
                          openApplication()
                        }}
                        color={theme.colors.textAndIcon[0]}
                        style={{ marginLeft: '2px' }}
                        size="xs"
                        variant="hover"
                      >
                        <IconClipboardText size={20} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Title>
              </>
            )}

            {!hideUsername && (
              <>
                {creatorUsername ? (
                  <Link
                    onClick={handleStopPropagation}
                    className={`${classes.user} ${utils.truncate}`}
                    to={profilePath}
                    // style={{ width: 'fit-content' }}
                    target="_self"
                    rel="noopener noreferrer"
                  >
                    {isAddress(creatorUsername) ? shortenHash(creatorUsername) : creatorUsername}
                  </Link>
                ) : (
                  <Text className={classes.description}>{shortenHash(userId)}</Text>
                )}
              </>
            )}
            {addInfo}
          </Stack>
        </>
      ) : userId ? (
        <Text className={classes.description}>{`${userId}`}</Text>
      ) : (
        <Text className={classes.description} style={{ color: theme.colors.paragraph[0] }}>
          {t('components.items.unknownCreator', 'Unknown Creator')}
        </Text>
      )}
      <ModalSkeleton
        title={t('components.governance.modal.acceptNomination.title', 'View Application')}
        onClose={closeApplication}
        opened={openedApplication}
      >
        <ViewApplicationModal
          nominee={userId}
          close={closeApplication}
          fillApplicationPeriodEnded={fillApplicationPeriodEnded}
        />
      </ModalSkeleton>
    </Stack>
  )
}

export default UserDetails
